import { z } from 'zod';

import {
  CohortBuilderItemChangedBy,
  CohortBuilderItemChangeStatusLegacy,
  SubjectVariableCriteriaSelectionTypeLegacy,
} from '../lib/cohortBuilderLegacy';

// ============================================================================
// Criteria Selection (unchanged)
// ============================================================================

const CriteriaSelectionValues = Object.values(SubjectVariableCriteriaSelectionTypeLegacy) as [
  string,
  ...string[]
];

const CriteriaSelectionSchema = z.enum(CriteriaSelectionValues);

export enum CriteriaSelection {
  INCLUSION = 'Inclusion',
  EXCLUSION = 'Exclusion',
  NOT_APPLIED = 'Not applied',
}

// ============================================================================
// v3 Enums
// ============================================================================

export enum OmopTable {
  PERSON = 'person',
  OBSERVATION_PERIOD = 'observation_period',
  CONDITION_OCCURRENCE = 'condition_occurrence',
  CONDITION_ERA = 'condition_era',
  DRUG_EXPOSURE = 'drug_exposure',
  DRUG_ERA = 'drug_era',
  DOSE_ERA = 'dose_era',
  PROCEDURE_OCCURRENCE = 'procedure_occurrence',
  DEVICE_EXPOSURE = 'device_exposure',
  MEASUREMENT = 'measurement',
  OBSERVATION = 'observation',
  DEATH = 'death',
  VISIT_OCCURRENCE = 'visit_occurrence',
  VISIT_DETAIL = 'visit_detail',
  NOTE = 'note',
  NOTE_NLP = 'note_nlp',
  SPECIMEN = 'specimen',
  EPISODE = 'episode',
  EPISODE_EVENT = 'episode_event',
  PROVIDER = 'provider',
  PAYER_PLAN_PERIOD = 'payer_plan_period',
  COST = 'cost',
}

export enum AggregationFunction {
  FIRST = 'first',
  LAST = 'last',
  MIN = 'min',
  MAX = 'max',
  MEAN = 'mean',
  MEDIAN = 'median',
  SUM = 'sum',
  COUNT = 'count',
  COUNT_DISTINCT = 'count_distinct',
  ANY = 'any',
  NTH = 'nth',
  // Reserved (schema-only, not compiled by SQL engine yet)
  DAYS_COVERED = 'days_covered',
  DAYS_TO_NTH = 'days_to_nth',
  CLOSEST_TO_REF = 'closest_to_ref',
  MODE = 'mode',
  DERIVED = 'derived',
}

export enum VariableOutputType {
  BOOLEAN = 'boolean',
  COUNT = 'count',
  NUMERIC = 'numeric',
  DATE = 'date',
  DURATION_DAYS = 'duration_days',
  TIME_TO_EVENT = 'time_to_event',
  CATEGORICAL = 'categorical',
  TEXT = 'text',
}

export enum AnchorType {
  INDEX_DATE = 'index_date',
  EVENT_DATE = 'event_date',
  FIXED_DATE = 'fixed_date',
  STUDY_PERIOD_START = 'study_period_start',
  STUDY_PERIOD_END = 'study_period_end',
}

export enum BooleanOperator {
  AND = 'and',
  OR = 'or',
  NOT = 'not',
}

export enum NumericOperator {
  EQ = 'eq',
  NEQ = 'neq',
  LT = 'lt',
  LTE = 'lte',
  GT = 'gt',
  GTE = 'gte',
  BETWEEN = 'between',
  NOT_BETWEEN = 'not_between',
}

export enum TextMatchMode {
  EXACT = 'exact',
  CONTAINS = 'contains',
  STARTS_WITH = 'starts_with',
  ENDS_WITH = 'ends_with',
  REGEX = 'regex',
  IN_LIST = 'in_list',
}

export enum TemporalOperator {
  // Point operators
  BEFORE = 'before',
  ON_OR_BEFORE = 'on_or_before',
  // Range / bidirectional
  BETWEEN = 'between',
  WITHIN = 'within',
  APART = 'apart',
  // Interval operators
  DURING = 'during',
  OVERLAPS = 'overlaps',
}

export enum CorrelatedRelationship {
  SAME_VISIT = 'same_visit',
  SAME_EPISODE = 'same_episode',
  TEMPORAL_DURING = 'temporal_during',
  SAME_PERSON_TEMPORAL = 'same_person_temporal',
  SAME_PROVIDER = 'same_provider',
  SAME_NOTE = 'same_note',
}

export enum TiebreakerRule {
  FIRST = 'first',
  ALPHABETICAL = 'alphabetical',
  NUMERIC = 'numeric',
  RANDOM = 'random',
  CUSTOM_HIERARCHY = 'custom_hierarchy',
}

export enum ConceptDomain {
  // Core clinical domains
  CONDITION = 'Condition',
  DRUG = 'Drug',
  PROCEDURE = 'Procedure',
  DEVICE = 'Device',
  MEASUREMENT = 'Measurement',
  OBSERVATION = 'Observation',
  VISIT = 'Visit',
  SPECIMEN = 'Specimen',
  EPISODE = 'Episode',
  // Demographic domains
  GENDER = 'Gender',
  RACE = 'Race',
  ETHNICITY = 'Ethnicity',
  // Attribute domains
  UNIT = 'Unit',
  ROUTE = 'Route',
  CONDITION_STATUS = 'Condition Status',
  TYPE_CONCEPT = 'Type Concept',
  MODIFIER = 'Modifier',
  MEAS_VALUE = 'Meas Value',
  CURRENCY = 'Currency',
  PAYER = 'Payer',
  PLAN = 'Plan',
  SPONSOR = 'Sponsor',
  REVENUE_CODE = 'Revenue Code',
  LANGUAGE = 'Language',
  // Additional OMOP domains
  COST = 'Cost',
  METADATA = 'Metadata',
  MEAS_VALUE_OPERATOR = 'Meas Value Operator',
  RELATIONSHIP = 'Relationship',
  PLACE_OF_SERVICE = 'Place of Service',
  SPEC_ANATOMIC_SITE = 'Spec Anatomic Site',
  SPEC_DISEASE_STATUS = 'Spec Disease Status',
  PLAN_STOP_REASON = 'Plan Stop Reason',
  PROVIDER = 'Provider',
  GEOGRAPHY = 'Geography',
  REGIMEN = 'Regimen',
  NOTE = 'Note',
  // Combo / cross-domain entries
  CONDITION_DEVICE = 'Condition/Device',
  DEVICE_PROCEDURE = 'Device/Procedure',
  OBS_PROCEDURE = 'Obs/Procedure',
  MEASUREMENT_OBS = 'Measurement/Obs',
  DEVICE_OBS = 'Device/Obs',
  CONDITION_MEAS = 'Condition/Meas',
  CONDITION_OBS = 'Condition/Obs',
  CONDITION_PROCEDURE = 'Condition/Procedure',
  DEVICE_DRUG = 'Device/Drug',
  DRUG_MEASUREMENT = 'Drug/Measurement',
  DRUG_OBS = 'Drug/Obs',
  CONDITION_DRUG = 'Condition/Drug',
  DRUG_PROCEDURE = 'Drug/Procedure',
  MEAS_PROCEDURE = 'Meas/Procedure',
}

// ============================================================================
// Concept Reference
// ============================================================================

const ConceptGroupRefSchema = z.object({
  type: z.literal('concept_group'),
  id: z.string().optional(),
  refIds: z.array(z.string()).min(1),
  name: z.string().nullable().optional(),
  domain: z.nativeEnum(ConceptDomain).nullable().optional(),
});

const InlineConceptListSchema = z.object({
  type: z.literal('inline_concept_list'),
  id: z.string().optional(),
  concepts: z
    .array(
      z.object({
        conceptId: z.number().int(),
        conceptName: z.string().nullable().optional(),
        domain: z.nativeEnum(ConceptDomain).nullable().optional(),
      })
    )
    .min(1),
  name: z.string().nullable().optional(),
  domain: z.nativeEnum(ConceptDomain).nullable().optional(),
});

const ConceptGroupListSchema = z.object({
  type: z.literal('concept_group_list'),
  id: z.string().optional(),
  names: z.array(z.string()).min(1),
  domain: z.nativeEnum(ConceptDomain).nullable().optional(),
});

const ConceptRefSchema = z.discriminatedUnion('type', [
  ConceptGroupRefSchema,
  InlineConceptListSchema,
  ConceptGroupListSchema,
]);

export type ConceptRef = z.infer<typeof ConceptRefSchema>;

// ============================================================================
// Value Types
// ============================================================================

const NumericRangeSchema = z
  .object({
    operator: z.nativeEnum(NumericOperator),
    value: z.number().nullable().optional(),
    low: z.number().nullable().optional(),
    high: z.number().nullable().optional(),
    lowInclusive: z.boolean().optional(),
    highInclusive: z.boolean().optional(),
  })
  .refine(
    (data) => {
      if (
        data.operator !== NumericOperator.BETWEEN &&
        data.operator !== NumericOperator.NOT_BETWEEN
      ) {
        return data.lowInclusive !== false && data.highInclusive !== false;
      }
      return true;
    },
    {
      message: 'lowInclusive/highInclusive are only valid with between/not_between operators',
    }
  );

export type NumericRange = z.infer<typeof NumericRangeSchema>;

const TextMatchSchema = z.object({
  mode: z.nativeEnum(TextMatchMode),
  value: z.string().nullable().optional(),
  values: z.array(z.string()).nullable().optional(),
  caseSensitive: z.boolean().default(false),
});

export type TextMatch = z.infer<typeof TextMatchSchema>;

const EpisodeDefinitionSchema = z.object({
  gapDays: z.number().int().nonnegative(),
});

export type EpisodeDefinition = z.infer<typeof EpisodeDefinitionSchema>;

/**
 * A single edge of a temporal window, resolved to a concrete date at execution time.
 *
 * Anchor semantics:
 *   - index_date:         patient's index/cohort entry date + offset
 *   - event_date:         date of referenced criterion + offset (requires anchorEventRef)
 *   - fixed_date:         literal calendar date (offset must be 0)
 *   - study_period_start: study observation period start + offset
 *   - study_period_end:   study observation period end + offset
 */
const WindowBoundSchema = z.object({
  anchor: z.nativeEnum(AnchorType).default(AnchorType.INDEX_DATE),
  anchorEventRef: z.string().nullable().optional(),
  fixedDate: z.string().nullable().optional(),
  offset: z.number().int().default(0),
});

export type WindowBound = z.infer<typeof WindowBoundSchema>;

/**
 * A time window defined by two explicit bounds. Each bound has its own anchor and offset.
 *
 * Examples:
 *   - 365 days before index to index:
 *       start=WindowBound(anchor=index_date, offset=-365)
 *       end=WindowBound(anchor=index_date, offset=0)
 *   - index to study end:
 *       start=WindowBound(anchor=index_date)
 *       end=WindowBound(anchor=study_period_end)
 */
const TemporalWindowSchema = z.object({
  start: WindowBoundSchema.default({
    anchor: AnchorType.STUDY_PERIOD_START,
    offset: 0,
  }),
  end: WindowBoundSchema.default({
    anchor: AnchorType.STUDY_PERIOD_END,
    offset: 0,
  }),
  timeUnit: z.literal('days').default('days'),
});

export type TemporalWindow = z.infer<typeof TemporalWindowSchema>;

// ============================================================================
// Domain Filters (passthrough — frontend round-trips logic opaquely)
// ============================================================================

/**
 * Domain filter for any OMOP CDM table.
 * Uses passthrough to accept all table-specific fields without validation.
 * The backend validates field details; the frontend stores and forwards.
 */
const DomainFilterSchema = z
  .object({
    table: z.nativeEnum(OmopTable),
  })
  .passthrough();

export type DomainFilter = z.infer<typeof DomainFilterSchema>;

/**
 * N-ary temporal constraint between filters' matched records.
 * Subsumes the legacy TemporalFilterPair.
 *
 * Modes:
 *   sequential:    filters[0] rel filters[1] rel filters[2] ... (chained, correlated)
 *   all_pairwise:  every pair (i,j) must satisfy the relationship (shared window)
 *
 * Self-join (filters.length === 1, selfJoin: true):
 *   The single filter is joined against itself to find patients with 2+ records.
 *
 * Cross-filter (filters.length >= 2):
 *   E.g., "Drug A started within 30 days after Condition B".
 */
export interface TemporalGroupShape {
  filters: Array<z.infer<typeof DomainFilterSchema> | FilterGroupShape | TemporalGroupShape>;
  relationship: TemporalOperator;
  mode: 'sequential' | 'all_pairwise';
  minGap?: number | null;
  maxGap?: number | null;
  timeUnit: 'days';
  pairLevel: 'event' | 'episode';
  selfJoin?: boolean;
  selectedFilter?: number | null;
  description?: string | null;
}

/**
 * Boolean combination of filters. Supports nested AND/OR/NOT.
 */
export interface FilterGroupShape {
  operator: BooleanOperator;
  filters: Array<z.infer<typeof DomainFilterSchema> | FilterGroupShape | TemporalGroupShape>;
  description?: string | null;
  episode?: z.infer<typeof EpisodeDefinitionSchema> | null;
}

const TemporalGroupSchema: z.ZodType<TemporalGroupShape> = z.lazy(() =>
  z.object({
    filters: z.array(z.union([DomainFilterSchema, FilterGroupSchema, TemporalGroupSchema])).min(1),
    relationship: z.nativeEnum(TemporalOperator),
    mode: z.enum(['sequential', 'all_pairwise']).default('sequential'),
    minGap: z.number().int().nullable().optional(),
    maxGap: z.number().int().nullable().optional(),
    timeUnit: z.literal('days').default('days'),
    pairLevel: z.enum(['event', 'episode']).default('event'),
    selfJoin: z.boolean().default(false),
    selectedFilter: z.number().int().nullable().optional(),
    description: z.string().nullable().optional(),
  })
);

export type TemporalGroup = z.infer<typeof TemporalGroupSchema>;

const FilterGroupSchema: z.ZodType<FilterGroupShape> = z.lazy(() =>
  z.object({
    operator: z.nativeEnum(BooleanOperator),
    filters: z.array(z.union([DomainFilterSchema, FilterGroupSchema, TemporalGroupSchema])).min(1),
    description: z.string().nullable().optional(),
    episode: EpisodeDefinitionSchema.nullable().optional(),
  })
);

export type FilterGroup = z.infer<typeof FilterGroupSchema>;

const LogicSchema = z.union([DomainFilterSchema, FilterGroupSchema, TemporalGroupSchema]);

// ============================================================================
// Aggregation
// ============================================================================

const PostFilterSchema = z.object({
  numeric: NumericRangeSchema.nullable().optional(),
  text: TextMatchSchema.nullable().optional(),
  conceptRef: ConceptRefSchema.nullable().optional(),
});

const RecordPrioritySchema = z.object({
  preferTypeConceptRef: ConceptRefSchema.nullable().optional(),
  preferSourceValues: TextMatchSchema.nullable().optional(),
  tieBreakBy: z.enum(['latest', 'earliest', 'arbitrary']).default('latest'),
});

const AggregationSpecSchema = z.object({
  function: z.nativeEnum(AggregationFunction),
  nthValue: z.number().int().nullable().optional(),
  postFilter: PostFilterSchema.nullable().optional(),
  recordPriority: RecordPrioritySchema.nullable().optional(),
  nullHandling: z.enum(['exclude', 'include', 'only_nulls']).default('exclude'),
  // Deduplication fields
  separationWindow: z.number().int().nullable().optional(),
  separationStrategy: z.enum(['rolling', 'fixed']).nullable().optional(),
  representativeDate: z.enum(['first', 'last']).nullable().optional(),
  // v3-7 Reserved fields — not yet supported by SQL lowering
  distinctField: z.string().nullable().optional(),
  gapDays: z.number().int().nullable().optional(),
  referenceAnchor: z.nativeEnum(AnchorType).nullable().optional(),
  includeRefDate: z.boolean().nullable().optional(),
  tiebreaker: z.nativeEnum(TiebreakerRule).nullable().optional(),
  valueHierarchy: z.array(z.string()).nullable().optional(),
  derivedAlgorithm: z.string().nullable().optional(),
});

export type AggregationSpec = z.infer<typeof AggregationSpecSchema>;

// ============================================================================
// Output
// ============================================================================

const BinDefinitionSchema = z.object({
  label: z.string(),
  low: z.number().nullable().optional(),
  high: z.number().nullable().optional(),
});

const ThresholdTransformSchema = z.object({
  transformType: z.literal('threshold'),
  operator: z.nativeEnum(NumericOperator),
  value: z.number(),
});

const BinTransformSchema = z.object({
  transformType: z.literal('bin'),
  bins: z.array(BinDefinitionSchema),
});

const CategoryMatchTransformSchema = z.object({
  transformType: z.literal('category_match'),
  targetValues: z.array(z.string()),
});

const DateComparisonTransformSchema = z.object({
  transformType: z.literal('date_comparison'),
  operator: z.nativeEnum(TemporalOperator),
  cutoffAnchor: z.nativeEnum(AnchorType),
  cutoffOffsetDays: z.number().int(),
});

const OutputTransformSchema = z.discriminatedUnion('transformType', [
  ThresholdTransformSchema,
  BinTransformSchema,
  CategoryMatchTransformSchema,
  DateComparisonTransformSchema,
]);

const VariableOutputSchema = z.object({
  type: z.nativeEnum(VariableOutputType),
  valueField: z.string().nullable().optional(),
  categories: z.record(z.string(), ConceptRefSchema).nullable().optional(),
  defaultValue: z.union([z.string(), z.number(), z.boolean()]).nullable().optional(),
  dateResolution: z
    .object({ fields: z.array(z.string()) })
    .nullable()
    .optional(),
  outputTransform: OutputTransformSchema.nullable().optional(),
});

export type VariableOutput = z.infer<typeof VariableOutputSchema>;

// ============================================================================
// Analytical Variable
// ============================================================================

/**
 * Reusable logic core — the stable, verified definition of a clinical concept.
 * Defined once in a library, referenced by multiple usages across studies.
 */
export const AnalyticalVariableBaseSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  // Nullable: SQL-only AVs (created via the de novo / agent path) have a
  // SQL_BODY but no structured logic tree. Consumers must skip-render rather
  // than hard-fail when logic is absent.
  logic: LogicSchema.nullable(),
  // Change tracking: set by RA workflow, cleared on save
  changeStatus: z.nativeEnum(CohortBuilderItemChangeStatusLegacy).optional(),
  changedBy: z.nativeEnum(CohortBuilderItemChangedBy).optional(),
});

export type AnalyticalVariableBase = z.infer<typeof AnalyticalVariableBaseSchema>;

/**
 * Fully-resolved analytical variable with all 4 orthogonal components:
 *   1. logic       — what records to select
 *   2. window      — when, relative to anchor
 *   3. aggregation — how to collapse + ordering + post-filters
 *   4. output      — type and shape of the result
 */
export const AnalyticalVariableSchema = AnalyticalVariableBaseSchema.extend({
  window: TemporalWindowSchema,
  aggregation: AggregationSpecSchema,
  output: VariableOutputSchema,
});

export type AnalyticalVariable = z.infer<typeof AnalyticalVariableSchema>;

// ============================================================================
// Usage Types (Criterion)
// ============================================================================

/**
 * Configuration for how an analytical variable is used as a criterion.
 * Optional overrides for aggregation function and output type.
 */
export const AnalyticalVariableUsageConfigSchema = z.object({
  aggregation: z.nativeEnum(AggregationFunction).optional().nullable(),
  outputFormat: z.nativeEnum(VariableOutputType).optional().nullable(),
});

export type AnalyticalVariableUsageConfig = z.infer<typeof AnalyticalVariableUsageConfigSchema>;

/**
 * Analytical variable criterion: references an AV by ID for use in I/E criteria.
 *
 * `analyticalVariableId` is nullable to match the backend DSL (PR #3647 +
 * Jawad's reference-by-id model). Three valid shapes:
 *   1. Direct, in-cohort AV: `analyticalVariableId` set, points at a paired
 *      entry in the cohort's `analyticalVariables` array. Legacy clone-on-add.
 *   2. Library reference: `analyticalVariableId` is null, `sourceLibraryAvId`
 *      set. The runner resolves the AV against the library at execution time;
 *      no IR is embedded in the cohort version DSL.
 *   3. Unbound stub: both null. Only valid in DRAFT cohorts produced by the
 *      cohort_drafter sub-agent.
 */
export const AnalyticalVariableCriterionSchema = AnalyticalVariableUsageConfigSchema.extend({
  criteriaType: z.literal('analytical_variable'),
  usageType: z.literal('criterion'),
  analyticalVariableId: z.string().uuid().nullable(),
  criteriaSelection: CriteriaSelectionSchema.default(CriteriaSelection.INCLUSION).describe(
    'Criteria selection for the analytical variable'
  ),
  // Library provenance: set on criteria added from the AV library. Required
  // for library-reference criteria (the runner uses this id to look up the
  // AV at execution time when `analyticalVariableId` is null).
  sourceLibraryAvId: z.string().uuid().nullable().optional(),
  // Plain-language description carried on unbound/stub criteria emitted by
  // the cohort_drafter — surfaced in the UI before an AV is bound. Optional
  // on resolved criteria.
  description: z.string().nullable().optional(),
  // Backend-owned user-visible title (OR-6077). On a stub it is the
  // description; the UI prefers the bound AV's name once one is attached.
  // Null only on pre-migration cohort versions.
  name: z.string().nullable().optional(),
});

export type AnalyticalVariableCriterion = z.infer<typeof AnalyticalVariableCriterionSchema>;

/**
 * @deprecated Use AnalyticalVariableCriterion instead
 */
export type AnalyticalVariableLegacy = AnalyticalVariableCriterion;
